import {
  Bullseye,
  Drawer,
  DrawerActions,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentBody,
  DrawerHead,
  DrawerPanelBody,
  DrawerPanelContent,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Label,
  Spinner,
  Title,
  Tooltip
} from '@patternfly/react-core';
import { HistoryIcon, LockIcon } from '@patternfly/react-icons';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

import { HistoryApi } from '@app/api';
import { ChangeAuthor } from '@app/components/ChangeAuthor';
import { DataLoadError } from '@app/components/DataLoadError';
import { HistoryPager } from '@app/components/HistoryPager';
import { formatStatusText } from '@app/components/KlassLabels';
import {
  EntityHistory,
  FieldChange,
  REVISION_TYPE_LABELS,
  Ref,
  RevisionEntry,
  ScalarValue,
  changeSummary,
  fieldLabel,
  isRef
} from '@app/model/History';
import { ApiError, isForbidden, isNotFound, toApiError } from '@app/utils/ApiError';
import { HISTORY_PAGE_SIZE } from '@app/utils/config';
import { ddMMyyyyFormat, ddMMyyyyHHmmFormat, ddMMyyyyHHmmTzFormat, fromIsoDate } from '@app/utils/functions';

export interface EntityChangeLogProps {
  /**
   * The registry slug the service knows the entity by - `class`, `instructor` - which is the same
   * segment its own resource is at. Drive it off HistoryApi.entities() rather than hard coding a
   * list, so an entity registered on the service side appears here without a change.
   */
  entity: string;
  /**
   * The row's **own** id. Not the offering id: a class detail page is addressed by offering id but
   * the audit trail is keyed on the primary key, so pass `klass.id`. Undefined while the page is
   * still loading its record, which renders nothing rather than requesting `/history/class/undefined`.
   */
  id?: number | string;
  /** What the reader is looking at, for the empty and forbidden states: "this class". */
  entityLabel?: string;
  /**
   * Fills in a Ref the service could not label. It labels a reference only when the row was
   * already loaded, and Skill and CustomFieldValue have no field it can label from at all, so an
   * instructor's skill changes arrive as bare ids - see Ref in model/History.
   *
   * Return undefined for anything not resolvable and the id is shown instead. For skills that is
   * only the rows recorded before the service stopped allowing one to be removed - see
   * skillRefLabel in model/Instructor.
   */
  resolveRef?(field: string, ref: Ref): string | undefined;
  isOpen: boolean;
  onClose(): void;
  /** The record named in the panel heading: "4711 - RH134". */
  title?: string;
  /** The page the panel opens over. */
  children: React.ReactNode;
}

/**
 * One record's change log, beside the record, as a panel over the page it belongs to.
 *
 * Wraps a whole detail page rather than sitting inside one of its sections, and that is the point:
 * the record stays on screen, so "status is Confirmed" and "status was set to Confirmed on the
 * 24th, by whom" are read together. A tab and a modal were both built and compared against this
 * one; both made that a two-step question, which is why this is the arrangement that stayed.
 *
 * The panel is not inline, so on a narrow viewport it covers the page instead of squeezing it - a
 * detail page's three-column description lists do not survive being narrowed.
 */
export const EntityChangeLog: React.FunctionComponent<EntityChangeLogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  ...logProps
}) => (
  <Drawer isExpanded={isOpen} onExpand={() => undefined}>
    <DrawerContent
      panelContent={
        // Wide enough that the table's own columns do not wrap: at 45% the timestamp broke over
        // two lines and the summary over four. Resizable from there.
        <DrawerPanelContent isResizable defaultSize="52%" minSize="32rem">
          <DrawerHead>
            <Title headingLevel="h2" size="lg">
              Change log{title ? ` — ${title}` : ''}
            </Title>
            <DrawerActions>
              <DrawerCloseButton onClick={onClose} />
            </DrawerActions>
          </DrawerHead>
          <DrawerPanelBody>
            {/* Only while open: mounting is what fetches, so a page whose panel has never been
                opened costs no request. */}
            {isOpen && <ChangeLogRevisions {...logProps} />}
          </DrawerPanelBody>
        </DrawerPanelContent>
      }
    >
      <DrawerContentBody>{children}</DrawerContentBody>
    </DrawerContent>
  </Drawer>
);

/** The half of EntityChangeLogProps that describes what to read, without the panel around it. */
type ChangeLogRevisionsProps = Omit<EntityChangeLogProps, 'isOpen' | 'onClose' | 'title' | 'children'>;

/**
 * Every recorded change to one record, newest first, read from GET /history/{entity}/{id}.
 *
 * The same rendering for every entity, which is the whole point of the service reporting a diff
 * rather than a snapshot: a revision is a list of fields that moved, in the same shape whatever
 * moved them, so a field added to a class or an instructor shows up here on its own. Anything that
 * would need a branch per entity belongs in a prop - see resolveRef - and not in this file.
 *
 * Three things the data forces, none of them obvious:
 *
 * - **The creating revision carries every field the record was created with** - 46 of them on the
 *   classes in the dev database, all reading `null -> value`. So rows summarise collapsed and the
 *   creation is described by its size rather than listed.
 * - **A revision can report no changes at all.** A `DEL` records the identity and nothing else,
 *   and an ordinary save where no audited field moved is also legitimately empty. Both are shown
 *   as the event they are; hiding them would misrepresent the sequence.
 * - **404 means "nothing was ever recorded for this id", not "no such page"**, and 403 means the
 *   record's history is not this user's to read. The service distinguishes them deliberately, so
 *   they get different states here.
 */
const ChangeLogRevisions: React.FunctionComponent<ChangeLogRevisionsProps> = ({
  entity,
  id,
  entityLabel = 'this record',
  resolveRef
}) => {
  const [history, setHistory] = React.useState<EntityHistory | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<ApiError | null>(null);
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());

  /**
   * The page, tied to the record it was turned to.
   *
   * Held with its own key rather than as a bare number so that pointing this at another record
   * starts at the newest revision again: page 3 of one class's history says nothing about the
   * next one's, and a stale page would open onto an empty table on any record with a shorter
   * trail. Read during render rather than reset in an effect, which would fetch the old page
   * first and the right one immediately after.
   */
  const key = `${entity}/${id}`;
  const [paging, setPaging] = React.useState({ key, page: 0 });
  const page = paging.key === key ? paging.page : 0;
  const setPage = (next: number) => setPaging({ key, page: next });

  const load = React.useCallback(async () => {
    if (id === undefined || id === null || id === '') return;

    setIsLoading(true);
    setError(null);
    try {
      setHistory(await HistoryApi.forEntity(entity, id, page, HISTORY_PAGE_SIZE));
    } catch (err) {
      setError(toApiError(err));
      setHistory(null);
    } finally {
      setIsLoading(false);
    }
  }, [entity, id, page]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // A revision number is unique across the whole trail, so the expansion set survives paging
  // without having to be cleared - going back to a page reopens what was open on it.
  const toggleExpanded = (revision: number) =>
    setExpanded(current => {
      const next = new Set(current);
      if (!next.delete(revision)) next.add(revision);
      return next;
    });

  if (id === undefined || id === null || id === '') return null;

  if (isLoading && !history) {
    return (
      <Bullseye>
        <Spinner aria-label="Loading change log" />
      </Bullseye>
    );
  }

  if (error && isForbidden(error)) {
    return (
      <Bullseye>
        <EmptyState headingLevel="h4" icon={LockIcon} titleText="Not yours to read">
          <EmptyStateBody>
            The change log of {entityLabel} is as restricted as the record itself, and your roles do
            not cover it.
          </EmptyStateBody>
        </EmptyState>
      </Bullseye>
    );
  }

  // Nothing has ever been recorded against this id. Not an error to report, and not the same as
  // an empty page further into a trail that does exist.
  if (error && isNotFound(error)) return <NoChangesRecorded entityLabel={entityLabel} />;

  if (error) {
    return <DataLoadError error={error} entityLabel="the change log" onRetry={() => void load()} />;
  }

  const revisions = history?.revisions ?? [];

  if (revisions.length === 0 && page === 0) return <NoChangesRecorded entityLabel={entityLabel} />;

  return (
    <React.Fragment>
      <Table aria-label={`Change log of ${entityLabel}`} variant="compact">
        <Thead>
          <Tr>
            <Th screenReaderText="Expand the fields that changed" />
            {/* Not narrower: PatternFly ellipsises the header itself below about this width, so
                the column reads "Chang…" before any data is in it. */}
            <Th width={20}>When</Th>
            <Th width={20}>Who</Th>
            <Th width={15}>Change</Th>
            <Th>Fields</Th>
          </Tr>
        </Thead>
        {revisions.map((revision, index) => {
          const isExpanded = expanded.has(revision.revision);
          const label = REVISION_TYPE_LABELS[revision.type];
          const hasChanges = revision.changes.length > 0;

          return (
            <Tbody key={revision.revision} isExpanded={isExpanded}>
              <Tr>
                <Td
                  expand={
                    // A revision with nothing to list gets no toggle at all, rather than one that
                    // opens onto an empty panel.
                    hasChanges
                      ? {
                          rowIndex: index,
                          isExpanded,
                          onToggle: () => toggleExpanded(revision.revision),
                          expandId: `revision-${revision.revision}`
                        }
                      : undefined
                  }
                />
                {/* The timestamp is one thing and reads badly split: the column is a percentage
                    of a panel narrower than the page, so it otherwise wraps after the date. */}
                <Td dataLabel="When" style={{ whiteSpace: 'nowrap' }}>
                  <Tooltip
                    content={`Revision ${revision.revision} — ${ddMMyyyyHHmmTzFormat(revision.changedAt)}`}
                  >
                    <span>{ddMMyyyyHHmmFormat(revision.changedAt)}</span>
                  </Tooltip>
                </Td>
                <Td dataLabel="Who"><ChangeAuthor username={revision.username} /></Td>
                <Td dataLabel="Change">
                  <Label isCompact color={label?.color ?? 'grey'}>{label?.text ?? revision.type}</Label>
                </Td>
                <Td dataLabel="Fields" modifier="breakWord">
                  {hasChanges
                    ? changeSummary(revision)
                    : <Subtle>{changeSummary(revision)}</Subtle>}
                </Td>
              </Tr>
              {isExpanded && hasChanges && (
                <Tr isExpanded>
                  <Td />
                  <Td dataLabel="Changed fields" colSpan={4}>
                    <ExpandableRowContent>
                      <RevisionChanges revision={revision} resolveRef={resolveRef} />
                    </ExpandableRowContent>
                  </Td>
                </Tr>
              )}
            </Tbody>
          );
        })}
      </Table>

      <HistoryPager
        page={page}
        count={revisions.length}
        hasMore={history?.hasMore ?? false}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </React.Fragment>
  );
};

const NoChangesRecorded: React.FunctionComponent<{ entityLabel: string }> = ({ entityLabel }) => (
  <Bullseye>
    <EmptyState headingLevel="h4" icon={HistoryIcon} titleText="No changes recorded">
      <EmptyStateBody>
        Nothing has been recorded against {entityLabel}. A record created before the audit trail
        began, and never touched since, reads this way too.
      </EmptyStateBody>
    </EmptyState>
  </Bullseye>
);

/** The fields one revision moved, as `label: before -> after`. */
const RevisionChanges: React.FunctionComponent<{
  revision: RevisionEntry;
  resolveRef?(field: string, ref: Ref): string | undefined;
}> = ({ revision, resolveRef }) => (
  <Table aria-label={`Fields changed in revision ${revision.revision}`} variant="compact" borders={false}>
    <Thead>
      <Tr>
        <Th width={25}>Field</Th>
        <Th width={35}>Before</Th>
        <Th width={40}>After</Th>
      </Tr>
    </Thead>
    <Tbody>
      {revision.changes.map(change => (
        <Tr key={change.field}>
          <Td dataLabel="Field">{fieldLabel(change.field)}</Td>
          {change.kind === 'COLLECTION' ? (
            // A collection is compared as a set, so there is no before and after to line up -
            // only what joined and what left. Both cells are given over to that rather than
            // pretending the columns mean the same thing here.
            <Td dataLabel="Changed" colSpan={2}>
              <CollectionChange change={change} resolveRef={resolveRef} />
            </Td>
          ) : (
            <React.Fragment>
              <Td dataLabel="Before" modifier="breakWord">
                <ChangeValue value={change.from} change={change} resolveRef={resolveRef} />
              </Td>
              <Td dataLabel="After" modifier="breakWord">
                <ChangeValue value={change.to} change={change} resolveRef={resolveRef} />
              </Td>
            </React.Fragment>
          )}
        </Tr>
      ))}
    </Tbody>
  </Table>
);

const CollectionChange: React.FunctionComponent<{
  change: FieldChange;
  resolveRef?(field: string, ref: Ref): string | undefined;
}> = ({ change, resolveRef }) => {
  const added = change.added ?? [];
  const removed = change.removed ?? [];

  if (added.length === 0 && removed.length === 0) return <Subtle>no members changed</Subtle>;

  return (
    <Flex spaceItems={{ default: 'spaceItemsXs' }}>
      {added.map((member, index) => (
        <FlexItem key={`added-${index}`}>
          <Label isCompact color="green" variant="outline">
            + {memberText(change.field, member, resolveRef)}
          </Label>
        </FlexItem>
      ))}
      {removed.map((member, index) => (
        <FlexItem key={`removed-${index}`}>
          <Label isCompact color="red" variant="outline">
            − {memberText(change.field, member, resolveRef)}
          </Label>
        </FlexItem>
      ))}
    </Flex>
  );
};

/** One value of a SCALAR, ENUM or REFERENCE change. */
const ChangeValue: React.FunctionComponent<{
  value: ScalarValue | Ref | undefined;
  change: FieldChange;
  resolveRef?(field: string, ref: Ref): string | undefined;
}> = ({ value, change, resolveRef }) => {
  // Never omitted when null, which is what makes this readable: `from: null` says the field had
  // no value, and there is no other way to tell that from "not applicable here".
  if (value === null || value === undefined) return <Subtle>not set</Subtle>;

  if (isRef(value)) return <React.Fragment>{refText(change.field, value, resolveRef)}</React.Fragment>;

  if (change.kind === 'ENUM') return <React.Fragment>{formatStatusText(String(value))}</React.Fragment>;

  return <React.Fragment>{scalarText(value)}</React.Fragment>;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * A scalar as a reader should see it. Dates travel as ISO 8601 text and are the only ones needing
 * a decision: a date-only value is formatted through fromIsoDate, because `new Date('2026-07-15')`
 * is defined to be midnight UTC and renders as the day before in any negative offset - the same
 * trap fromIsoDate exists for everywhere else in the app.
 */
const scalarText = (value: ScalarValue): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (typeof value === 'string') {
    if (ISO_DATE.test(value)) return ddMMyyyyFormat(fromIsoDate(value));
    if (ISO_DATE_TIME.test(value)) return ddMMyyyyHHmmFormat(value);
    return value;
  }

  return String(value);
};

/**
 * A reference as text. The service labels one only when it already had the row loaded, so an
 * unlabelled reference is a display problem and never a missing reference - the id is always
 * there. Shown as `Field #id` when nothing can label it, which is honest and still points at
 * the row.
 */
const refText = (
  field: string,
  ref: Ref,
  resolveRef?: (field: string, ref: Ref) => string | undefined
): string => ref.label ?? resolveRef?.(field, ref) ?? `${fieldLabel(field)} #${ref.id}`;

const memberText = (
  field: string,
  member: Ref | ScalarValue,
  resolveRef?: (field: string, ref: Ref) => string | undefined
): string => (isRef(member) ? refText(field, member, resolveRef) : scalarText(member as ScalarValue));

const Subtle: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>{children}</span>
);
